import React, { useCallback, useEffect, useState } from 'react'
import { isEmpty } from 'ramda'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { useMutation } from '@tanstack/react-query'
import themesConfig from 'app/fuse-configs/themesConfig'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import {
  Dialog,
  MenuItem,
  IconButton,
  Typography,
  FormControlLabel,
} from '@material-ui/core'

import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import { CLOSE, SECONDARY } from 'app/constants/button'

import CheckBox from 'app/componentV4/CheckBox'
import Selector from 'app/componentV4/Input/Selector'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'

import {
  raws,
  allDay,
  openHours,
  renderLabel,
  defaultData,
  generateTimeOptions,
  handleInitialValues,
  getLabelId,
} from './helper'

const OpeningHours = () => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const timeOptions = generateTimeOptions()

  const [open, setOpen] = useState(false)
  const [raw, setRaw] = useState(() => raws(defaultData))
  const [initialValues, setInitialValues] = useState(() =>
    handleInitialValues(raw),
  )

  const handleInitialData = useCallback((data) => {
    const rawData = raws(data)
    setRaw(rawData)
    setInitialValues(handleInitialValues(rawData))
  }, [])

  const fetchOpeningHours = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.profileInfo.getOpeningHours()
      return response.data
    },
    onSuccess: (data) => {
      const openData = isEmpty(data) ? defaultData : data
      handleInitialData(openData)
    },
    onError: (error) => {
      const msgError = error.response.data
      dispatch(MessageAlert.showNetworkError(formatMessage)(msgError))
    },
  })

  const updateOpeningHours = useMutation({
    mutationFn: async (values) => {
      const response = await effects.apis.profileInfo.updateOpeningHours({
        openingHours: values,
      })
      return response.data
    },
    onSuccess: async () => {
      await fetchOpeningHours.mutate()
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'text.change.success' }),
        }),
      )
      setOpen(false)
    },
    onError: (error) => {
      const msgError = error.response.data
      dispatch(MessageAlert.showNetworkError(formatMessage)(msgError))
    },
  })

  useEffect(() => {
    fetchOpeningHours.mutate()
  }, [])

  return (
    <>
      <div className="bg-white p-10 border-1 border-primary-350 rounded-5 rounded-tr-5 overflow-x-auto">
        <div className="flex justify-between items-center">
          <Typography className="text-14 font-500 text-primary flex items-center">
            {formatMessage({ id: 'profile.info.hours' })}
          </Typography>
          <IconButton onClick={() => setOpen(true)}>
            <EditOutlinedIcon
              fontSize="small"
              style={{ color: themesConfig.default.colors['primary'] }}
            />
          </IconButton>
        </div>
        <div className="mt-4 space-y-2">
          {initialValues.data.map((entry, i) => (
            <div
              key={i}
              className="md:flex justify-between items-center my-5 md:my-0"
            >
              <Typography className="capitalize text-14 text-primary">
                {formatMessage({ id: getLabelId(entry.openDay) })}
              </Typography>
              <Typography className="text-14 text-primary font-bold md:font-normal">
                {renderLabel(entry)}
              </Typography>
            </div>
          ))}
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 16, padding: '3rem' } }}
      >
        <div className="flex justify-between">
          <Typography className="text-16 font-500 text-primary flex items-center">
            {formatMessage({ id: 'profile.info.hours' })}
          </Typography>
          <IconButton
            aria-label="close-opening-hours-dialog"
            onClick={() => setOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            const payload = values.data.map(
              ({ openDay, checked, openTime, closeTime }) => {
                if (checked === openHours.CLOSE_HOURS) {
                  return {
                    openDay,
                    checked: openHours.CLOSE_HOURS,
                    openTime: null,
                    closeTime: null,
                  }
                }
                if (allDay(openTime, closeTime)) {
                  return {
                    openDay,
                    checked: openHours.OPEN_HOURS,
                    openTime: openHours.ALL_DAY,
                    closeTime: openHours.ALL_DAY,
                  }
                }
                return {
                  openDay,
                  checked: openHours.OPEN_HOURS,
                  openTime,
                  closeTime,
                }
              },
            )
            updateOpeningHours.mutate(payload)
          }}
        >
          {({ values, dirty, setFieldValue }) => (
            <Form>
              {values.data.map((dayEntry, idx) => {
                const isClosed = dayEntry.checked === openHours.CLOSE_HOURS
                const is24 = allDay(dayEntry.openTime, dayEntry.closeTime)
                const openIndex = timeOptions.findIndex(
                  (t) => t === dayEntry.openTime,
                )
                const closeIndex = timeOptions.findIndex(
                  (t) => t === dayEntry.closeTime,
                )

                return (
                  <div key={idx} className="flex items-center my-10">
                    <div className="w-[155px]">
                      <Typography className="text-14 text-secondary-300 flex items-center">
                        {formatMessage({ id: `weekday.${dayEntry.openDay}` })}
                      </Typography>
                      <FormControlLabel
                        className="capitalize"
                        control={
                          <CheckBox
                            checked={isClosed}
                            onChange={(e) => {
                              const closedNow = e.target.checked
                              setFieldValue(
                                `data.${idx}.checked`,
                                closedNow
                                  ? openHours.CLOSE_HOURS
                                  : openHours.OPEN_HOURS,
                              )
                              if (closedNow) {
                                setFieldValue(
                                  `data.${idx}.openTime`,
                                  openHours.TIME_START_DAY,
                                )
                                setFieldValue(
                                  `data.${idx}.closeTime`,
                                  openHours.TIME_END_DAY,
                                )
                              } else {
                                const orig = raw[idx]
                                setFieldValue(
                                  `data.${idx}.openTime`,
                                  orig.openTime || openHours.TIME_START,
                                )
                                setFieldValue(
                                  `data.${idx}.closeTime`,
                                  orig.closeTime || openHours.TIME_END,
                                )
                              }
                            }}
                          />
                        }
                        label={formatMessage({ id: 'label.closed' })}
                      />
                    </div>
                    {is24 && !isClosed ? (
                      <div>
                        <Typography className="text-12 text-primary flex items-center">
                          <FormattedMessage id="openAt.label" />
                        </Typography>
                        <Selector
                          labelId={`hours-${idx}`}
                          value={dayEntry.openTime}
                          onChange={(e) => {
                            const val = e.target.value
                            setFieldValue(`data.${idx}.openTime`, val)
                            setFieldValue(`data.${idx}.closeTime`, val)
                          }}
                          className="w-[150px]"
                          disabled={isClosed}
                        >
                          {timeOptions.map((t) => (
                            <MenuItem
                              key={t}
                              value={t}
                              /* Only disable when closed */
                              disabled={isClosed}
                            >
                              {t}
                            </MenuItem>
                          ))}
                        </Selector>
                      </div>
                    ) : (
                      <div className="sm:flex sm:justify-between sm:w-xs space-y-3 sm:space-y-0">
                        <div>
                          <Typography className="text-12 text-primary flex items-center">
                            <FormattedMessage id="openAt.label" />
                          </Typography>
                          <Selector
                            labelId={`open-${idx}`}
                            value={dayEntry.openTime}
                            onChange={(e) =>
                              setFieldValue(
                                `data.${idx}.openTime`,
                                e.target.value,
                              )
                            }
                            className="w-[150px]"
                            disabled={isClosed}
                          >
                            {timeOptions.map((t, optIdx) => (
                              <MenuItem
                                key={t}
                                value={t}
                                disabled={isClosed || optIdx >= closeIndex}
                              >
                                {t}
                              </MenuItem>
                            ))}
                          </Selector>
                        </div>
                        <div>
                          <Typography className="text-12 text-primary flex items-center">
                            <FormattedMessage id="closeAt.label" />
                          </Typography>
                          <Selector
                            labelId={`close-${idx}`}
                            value={dayEntry.closeTime}
                            onChange={(e) =>
                              setFieldValue(
                                `data.${idx}.closeTime`,
                                e.target.value,
                              )
                            }
                            className="w-[150px]"
                            disabled={isClosed}
                          >
                            {timeOptions.map((t, optIdx) => (
                              <MenuItem
                                key={t}
                                value={t}
                                disabled={isClosed || optIdx <= openIndex}
                              >
                                {t}
                              </MenuItem>
                            ))}
                          </Selector>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              <div className="flex justify-center space-x-5 mt-10">
                <OutlinedButtons color={CLOSE} onClick={() => setOpen(false)}>
                  <FormattedMessage id="cancelBtn" />
                </OutlinedButtons>
                <ContainedButton
                  type="submit"
                  color={SECONDARY}
                  disabled={
                    !dirty ||
                    updateOpeningHours.isPending ||
                    fetchOpeningHours.isPending
                  }
                  isLoading={updateOpeningHours.isPending}
                >
                  <FormattedMessage id="subscriberCrm.editGroup.save.label" />
                </ContainedButton>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

export default OpeningHours
